import { Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import Layout from "@/components/application/Layout";
import LoadingScreen from "@/components/ui/LoadingScreen";
import PrivateRoute from "./routes/PrivateRoute";
import NewPassword from "@/pages/auth/RecoveryFlow/new-password.tsx";
import MyTeams from "@/pages/team/MyTeams.tsx";

const AUTH_ROUTES = ["/auth", "/register", "/recovery", "/change-password"];

// Lazy loading das páginas
const HomeLazy = lazy(() => import("@/pages/home"));
const AuthLazy = lazy(() => import("@/pages/auth/Login"));
const RegisterFormLazy = lazy(
  () => import("@/pages/auth/Register/RegisterForm")
);
const RecoveryPasswordLazy = lazy(
  () => import("@/pages/auth/RecoveryFlow/recovery-password")
);
const ProfileLazy = lazy(() => import("@/pages/profile"));
const ProfileInvitesLazy = lazy(() => import("@/pages/profile/invites"));
const TeamProfileLazy = lazy(
  () => import("@/pages/profile/TeamProfileLanding")
);
const ChampionshipsLazy = lazy(() => import("@/pages/championship"));
const ChampionshipDetailsLazy = lazy(
  () => import("@/pages/championship-details")
);
const TablePageLazy = lazy(
  () => import("@/pages/championship-details/TablePage")
);
const CreateTeamLazy = lazy(() => import("./pages/team/createTeam"));
const CheckoutLazy = lazy(() => import("@/pages/checkout"));
const PaymentSuccessLazy = lazy(() => import("@/pages/checkout/success"));
const NotificationsLazy = lazy(() => import("@/pages/notifications"));
const RankingLazy = lazy(() => import("@/pages/ranking"));
const ChatLazy = lazy(() => import("@/pages/chat"));
const DashboardLazy = lazy(() => import("@/pages/Dashboard/Dashboard.tsx"));
const SettingsLazy = lazy(() => import("@/pages/settings"));
const SupportLazy = lazy(() => import("@/pages/support"));
const WelcomeLazy = lazy(() => import("@/pages/welcome"));
const NotFoundLazy = lazy(() => import("@/pages/not-found"));
const EventsLazy = lazy(() => import("@/pages/events"));
const PlayersLazy = lazy(() => import("@/pages/players"));
const TeamJobsLazy = lazy(() => import("@/pages/team-jobs/TeamJobsPage"));

const EventDetailsLazy = lazy(() => import("@/pages/events/details"));

const TeamsLazy = lazy(() => import("@/pages/team/MyTeams.tsx"));
const TeamDetailsLazy = lazy(() => import("@/pages/team/DashTeamManager"));
const TeamRecruitmentLazy = lazy(
  () => import("@/pages/team/TeamRecruitmentPage")
);

function AppRoutes() {
  const location = useLocation();

  // Verifica se a página atual pertence às rotas de autenticação
  const isAuthPage = AUTH_ROUTES.includes(location.pathname);

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {isAuthPage ? (
          <>
            <Route path="/auth" element={<AuthLazy />} />
            <Route path="/register" element={<RegisterFormLazy />} />
            <Route path="/recovery" element={<RecoveryPasswordLazy />} />
            <Route path="/change-password" element={<NewPassword />} />
          </>
        ) : (
          <Route element={<Layout />}>
            <Route path="/" element={<ChampionshipsLazy />} />
            <Route path="/home" element={<HomeLazy />} />
            <Route path="/profile" element={<ProfileLazy />} />
            <Route
              path="/profile/invites"
              element={
                <PrivateRoute>
                  <ProfileInvitesLazy />
                </PrivateRoute>
              }
            />
            <Route path="/profile/:id" element={<ProfileLazy />} />
            <Route path="/pro/:nickname" element={<ProfileLazy />} />
            <Route path="/championships" element={<ChampionshipsLazy />} />

            <Route
              path="/championships/:url"
              element={<ChampionshipDetailsLazy />}
            />
            <Route
              path="/championships/:url/checkout"
              element={<CheckoutLazy />}
            />
            <Route
              path="/championships/payment/:id"
              element={<CheckoutLazy />}
            />
            <Route
              path="/championships/payment/:id/team/:teamId"
              element={<CheckoutLazy />}
            />
            <Route
              path="/championships/table/:id"
              element={<TablePageLazy />}
            />

            <Route
              path="/team/create"
              element={
                <PrivateRoute>
                  <CreateTeamLazy />
                </PrivateRoute>
              }
            />

            <Route
              path="/checkout/success"
              element={
                <PrivateRoute>
                  <PaymentSuccessLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <NotificationsLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/ranking"
              element={
                <PrivateRoute>
                  <RankingLazy />{" "}
                </PrivateRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <PrivateRoute>
                  <ChatLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <SettingsLazy />{" "}
                </PrivateRoute>
              }
            />
            <Route path="/team/create" element={<CreateTeamLazy />} />
            <Route path="/checkout/success" element={<PaymentSuccessLazy />} />
            <Route path="/notifications" element={<NotificationsLazy />} />
            <Route path="/ranking" element={<RankingLazy />} />
            <Route path="/chat" element={<ChatLazy />} />
            <Route path="/settings" element={<SettingsLazy />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/players"
              element={
                <PrivateRoute>
                  <PlayersLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/support"
              element={
                <PrivateRoute>
                  <SupportLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <PrivateRoute>
                  <TeamsLazy />
                </PrivateRoute>
              }
            />
            <Route
              path="/my/teams"
              element={
                <PrivateRoute>
                  <MyTeams />
                </PrivateRoute>
              }
            />
            <Route path="/welcome" element={<WelcomeLazy />} />

            <Route
              path="/my/teams/:tag"
              element={
                <PrivateRoute>
                  <TeamDetailsLazy />
                </PrivateRoute>
              }
            />

            <Route path="/team/profile/:tag" element={<TeamProfileLazy />} />

            <Route
              path="/team/:tag/recruitment"
              element={
                <PrivateRoute>
                  <TeamRecruitmentLazy />
                </PrivateRoute>
              }
            />

            <Route path="/events" element={<EventsLazy />} />
            <Route
              path="/events/:context/:name"
              element={<EventDetailsLazy />}
            />

            <Route path="/jobs" element={<TeamJobsLazy />} />

            <Route
              path="/team-jobs/:teamId"
              element={
                <PrivateRoute>
                  <TeamJobsLazy />
                </PrivateRoute>
              }
            />

            {/* 404 - Not Found Page */}
            <Route path="*" element={<NotFoundLazy />} />
          </Route>
        )}
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
